import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type PointerEvent,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { ArrowSimple, Copy, t } from '@stokr/components-library'
import { TokenIcon } from 'components/sto/chart'
import { hapticTick } from 'utils/haptics'
import { useProductSwitcher, type ProductSwitcherItem } from 'hooks/sto/useProductSwitcher'
import { SheetClose, SheetHeader } from 'components/sto/KeyActions/KeyActions.styles'
import { TickerBadge } from './Hero.styles'
import {
  Backdrop,
  Hint,
  HOLD_MS,
  IconRing,
  List,
  Ring,
  RING_BOX,
  RING_RADIUS,
  Row,
  RowIcon,
  RowMeta,
  RowText,
  RowTitle,
  SelectedTag,
  Sheet,
  SheetHead,
  SheetTitle,
  Trigger,
  TriggerWrap,
} from './ProductSwitcher.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

/** How long the "hold" hint stays after a tap. */
const HINT_MS = 1200
/** After the page has loaded, the chip wiggles once, to invite a press. */
const INTRO_DELAY_MS = 1200

const subscribe = () => () => {}

/**
 * While open: Escape closes, the page behind does not scroll, focus goes in. On close, focus goes
 * back to the chip for keyboard use only; after a touch or click the browser would ring it.
 */
function useSheetBehaviour(
  open: boolean,
  close: () => void,
  sheetRef: RefObject<HTMLDivElement | null>,
  triggerRef: RefObject<HTMLButtonElement | null>,
  byKeyboard: RefObject<boolean>,
) {
  useEffect(() => {
    if (!open) return undefined
    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = 'hidden'
    sheetRef.current?.focus({ preventScroll: true })
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      byKeyboard.current = true
      close()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      root.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKey)
      if (byKeyboard.current) triggerRef.current?.focus({ preventScroll: true })
      else if (sheetRef.current?.contains(document.activeElement)) (document.activeElement as HTMLElement).blur()
    }
  }, [open, close, sheetRef, triggerRef, byKeyboard])
}

const ProductRow = ({ item, onPick }: { item: ProductSwitcherItem; onPick: () => void }) => {
  const trackSto = useStoTrack()
  const content = (
    <>
      <RowIcon aria-hidden="true">
        <TokenIcon key={item.icons.join('|')} sources={item.icons} size={32} bare />
      </RowIcon>
      <RowText>
        <RowTitle>{item.title}</RowTitle>
        <RowMeta $selected={item.selected}>
          {item.ticker && `${item.ticker} · `}
          <Copy id={item.typeLabelKey} />
        </RowMeta>
      </RowText>
      {item.selected && (
        <SelectedTag>
          <Copy id="stoPage.productSwitcher.selected" />
        </SelectedTag>
      )}
    </>
  )
  return (
    <li>
      {item.selected ? (
        <Row $selected aria-current="page">
          {content}
        </Row>
      ) : (
        <Row
          as={Link}
          to={`/${item.slug}`}
          data-cy={`sto-product-switcher-item-${item.slug}`}
          onClick={() => {
            trackSto('sto_product_switched', { to_project: item.slug })
            onPick()
          }}>
          {content}
        </Row>
      )}
    </li>
  )
}

/**
 * Mobile: the ticker chip, held to switch product. A tap says to hold; holding fills a ring
 * around the icon, and once full the product list opens as a bottom sheet. Keyboard: Enter or
 * Space opens it straight away.
 */
export const ProductSwitcherChip = ({ ticker, icons }: { ticker: string; icons: string[] }) => {
  const items = useProductSwitcher()
  const canSwitch = items.length > 1
  const [open, setOpen] = useState(false)
  const [holding, setHolding] = useState(false)
  // Taps since the hint appeared: each one restarts its timer, so fast taps keep it up.
  const [hintTaps, setHintTaps] = useState(0)
  const hint = hintTaps > 0
  const [shaking, setShaking] = useState(false)
  // The list has scrolled under the sheet's header: it gets a shadow.
  const [scrolled, setScrolled] = useState(false)
  const holdTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const trackSto = useStoTrack()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  // Escape, or opened from the keyboard: then focus goes back to the chip on close.
  const openedByKeyboard = useRef(false)
  const titleId = useId()
  // The sheet goes to <body>, so it only renders in the browser.
  const isClient = useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  )

  const close = useCallback(() => setOpen(false), [])
  useSheetBehaviour(open, close, sheetRef, triggerRef, openedByKeyboard)

  useEffect(() => () => clearTimeout(holdTimer.current), [])

  // A moment after the page has fully loaded, every load. No haptic: iOS only allows one from a tap.
  useEffect(() => {
    if (!canSwitch) return undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    const start = () => {
      timer = setTimeout(() => setShaking(true), INTRO_DELAY_MS)
    }
    if (document.readyState === 'complete') start()
    else window.addEventListener('load', start, { once: true })
    return () => {
      clearTimeout(timer)
      window.removeEventListener('load', start)
    }
  }, [canSwitch])
  useEffect(() => {
    if (!hintTaps) return undefined
    const timer = setTimeout(() => setHintTaps(0), HINT_MS)
    return () => clearTimeout(timer)
  }, [hintTaps])

  const openSheet = (byKeyboard = false) => {
    openedByKeyboard.current = byKeyboard
    trackSto('sto_product_switcher_opened', { via: byKeyboard ? 'keyboard' : 'hold', products: items.length })
    setHintTaps(0)
    setOpen(true)
  }

  const stopHold = () => {
    clearTimeout(holdTimer.current)
    holdTimer.current = undefined
    setHolding(false)
  }

  const onPointerDown = (event: PointerEvent) => {
    if (!canSwitch || (event.pointerType === 'mouse' && event.button !== 0)) return
    setHolding(true)
    holdTimer.current = setTimeout(() => {
      holdTimer.current = undefined
      setHolding(false)
      // Android buzzes here; iOS ignores a haptic from a timer.
      hapticTick()
      openSheet()
    }, HOLD_MS)
  }

  // Let go before the ring filled: a tap, so say what to do instead.
  const onPointerUp = () => {
    if (!holdTimer.current) return
    stopHold()
    setHintTaps((taps) => taps + 1)
    setShaking(true)
    hapticTick()
  }

  return (
    <>
      <TriggerWrap>
        <Trigger
          ref={triggerRef}
          type="button"
          aria-label={`${ticker}, ${t('stoPage.productSwitcher.trigger')}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-cy="sto-product-switcher-trigger"
          data-holding={holding}
          data-shake={shaking}
          onAnimationEnd={() => setShaking(false)}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          onContextMenu={(event) => event.preventDefault()}
          onDragStart={(event) => event.preventDefault()}
          // Keyboard activation (no pointer): open straight away.
          onClick={(event) => {
            if (event.detail === 0 && canSwitch) openSheet(true)
          }}>
          <TickerBadge $placement="breadcrumb">
            {ticker}
            <IconRing>
              <TokenIcon key={icons.join('|')} sources={icons} size={32} bare />
              <Ring $holding={holding} viewBox={`0 0 ${RING_BOX} ${RING_BOX}`} aria-hidden="true">
                <circle cx={RING_BOX / 2} cy={RING_BOX / 2} r={RING_RADIUS} />
              </Ring>
            </IconRing>
          </TickerBadge>
        </Trigger>
        {/* Always rendered, so it fades out with its text. */}
        <Hint $visible={hint} aria-hidden={!hint}>
          <Copy id="stoPage.productSwitcher.hint" />
        </Hint>
      </TriggerWrap>

      {isClient &&
        createPortal(
          <>
            <Backdrop $open={open} onClick={close} aria-hidden="true" />
            <Sheet
              ref={sheetRef}
              $open={open}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              tabIndex={-1}
              inert={!open}
              onScroll={(event) => setScrolled(event.currentTarget.scrollTop > 0)}>
              <SheetHead $scrolled={scrolled}>
                <SheetHeader>
                  <SheetTitle id={titleId}>
                    <Copy id="stoPage.productSwitcher.title" />
                  </SheetTitle>
                  <SheetClose
                    type="button"
                    aria-label={t('common.close')}
                    data-cy="sto-product-switcher-close"
                    onClick={close}>
                    <ArrowSimple width={12} height={8} aria-hidden="true" />
                  </SheetClose>
                </SheetHeader>
              </SheetHead>
              <List>
                {items.map((item) => (
                  <ProductRow key={item.slug} item={item} onPick={close} />
                ))}
              </List>
            </Sheet>
          </>,
          document.body,
        )}
    </>
  )
}
